import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  ANNUAL_MULTI_YEAR,
  CURRENT_YEAR,
  MULTI_YEAR,
  N_YEARS,
} from '../constants/years';
import { useFarmSourceContext } from '../contexts/FarmSourceContext';
import { useFilterContext } from '../contexts/FilterContext';
import {
  CurrentUserQuery,
  FarmActionsQuery,
  FarmHasSubsidiesQuery,
  FarmQuery,
  FarmSubscriptionQuery,
  TeamRemainingPaidSeatsQuery,
} from '../queries';
import { getFirstMutationResult, getFirstQueryResult } from './graphql';
import {
  farmCanBeEdited,
  getIsFarmPath,
  getIsTeamPath,
  timeseriesArray,
} from './utils';
import useMutateWithState from '../hooks/useMutateWithState';

/*
Get roles for conditional UI elements.
This doesn't actually control access to resources,
those restrictions need to be in the backend.
*/
export const useUserRole = () => {
  const { data } = useQuery(CurrentUserQuery);
  return {
    isAuthenticated: data?.me || false,
    isSuperadmin: data?.me?.superadminRole || false,
  };
};

export const useUser = () => {
  const { data } = useQuery(CurrentUserQuery);
  return data?.me || {};
};

export const useUserSubscriptionStatus = () => {
  const { data } = useQuery(CurrentUserQuery);
  return data?.me?.hasValidSubscription || false;
};

export const useRemainingPaidSeats = (teamId, skip = false) => {
  const { data } = useQuery(TeamRemainingPaidSeatsQuery, {
    variables: { id: teamId },
    skip: skip || !teamId,
  });
  return {
    remainingPaidSeats: data?.teamStripeState?.remainingPaidSeats || 0,
    numPaidSeats: data?.teamStripeState?.numPaidSeats || 0,
  };
};

/*
Add the farmId from the URL to the query
If includeFarmId is false, this will not be included
This lets us use this hook
In places where the query could be for a template or override
*/
export const useQueryWithFarmId = (
  query,
  { includeFarmId = true, ...options } = {},
) => {
  const { farmId } = useParams();
  return useQuery(query, {
    ...options,
    variables: {
      ...options.variables,
      farmId: includeFarmId ? farmId : undefined,
    },
    skip: includeFarmId && !farmId,
  });
};

/*
Returns the start year for the farm from the Current User query,
or the current year if unavailable.
or if not in the context of a farm
*/
export const useStartYear = () => {
  const { farmId } = useParams();
  const { loading, error, data } = useQueryWithFarmId(FarmQuery);
  return !farmId || loading || error
    ? CURRENT_YEAR // Default to current year
    : data.farm.startYear;
};

/*
Returns the name for the current farm, with loading and error messages
*/
export const useFarmName = (skip = false) => {
  const { loading, error, data } = useQueryWithFarmId(FarmQuery, {
    skip,
    context: { priorityOp: true },
  });
  if (loading) return 'Loading...';
  if (error) {
    console.error(error.message);
    return 'Error loading farm';
  }
  return data?.farm.name;
};

/*
Returns the address for the current farm, with loading and error messages
*/
export const useFarmAddress = (skip = false) => {
  const { loading, error, data } = useQueryWithFarmId(FarmQuery, {
    skip,
    context: { priorityOp: true },
  });
  if (loading) return 'Loading...';
  if (error) {
    console.error(error.message);
    return 'Error loading farm';
  }
  return data?.farm.address;
};

/* Returns true if the farm has subsidies, false when loading or errored */
export const useFarmHasSubsidies = () => {
  const { data } = useQueryWithFarmId(FarmHasSubsidiesQuery, {
    // Ensures that the query is updated after subsidy or farm data changes
    fetchPolicy: 'cache-and-network',
  });
  const { hasSubsidies = false } = data?.farm || {};
  return hasSubsidies;
};

const useQueryWithRequiredFarmId = (query, farmId) => {
  return useQuery(query, {
    variables: { farmId },
    skip: !farmId,
  });
};

export const useUserFarmActions = (farmId) => {
  const { data } = useQueryWithRequiredFarmId(FarmActionsQuery, farmId);
  return data?.farm?.allowedActions || {};
};

// a farm can be edited if it has a valid subscription and is not archived or locked
export const useFarmCanBeEdited = (farmId) => {
  const { data } = useQueryWithRequiredFarmId(FarmSubscriptionQuery, farmId);
  return farmCanBeEdited(data?.farm);
};

// a user can edit a farm if they have permission to do so and the farm can be edited
export const useUserCanEditFarm = (farmId) => {
  const { canEdit } = useUserFarmActions(farmId);
  const farmEditable = useFarmCanBeEdited(farmId);
  return canEdit && farmEditable;
};

/*
Returns a memoized array of ordered years
with a min year of the context Farm's start year, or startYear if passed in
and a number of years based on either the length argument, the FilterContext "years" if it exists, or N_YEARS
*/
export const useYearsArray = (length, startYear = null) => {
  const farmStartYear = useStartYear();
  const { years } = useFilterContext().state;
  const nYears = length || years || N_YEARS;
  return useMemo(
    () =>
      timeseriesArray({ length: nYears, start: startYear || farmStartYear }),
    [nYears, startYear, farmStartYear],
  );
};

/*
Returns a memoized function that filters an array based on the current
year filter from the filter context.
*/
export const useFilterYears = () => {
  const { years, year } = useFilterContext().state;
  return useCallback(
    (array) =>
      year === MULTI_YEAR || year === ANNUAL_MULTI_YEAR
        ? array.slice(0, years)
        : [array[year]],
    [year, years],
  );
};

const processResultForMessage = ({ loading, error, data, ...rest }) => {
  const msg = error ? 'Error loading data' : loading ? 'Loading...' : '';
  if (error) console.error(error);
  return {
    data,
    msg,
    ...rest,
  };
};

/*
Returns a loading or error message if the data isn't present. Either
msg or data will be set.
*/
export const useQueryWithMsg = (query, options) =>
  processResultForMessage(useQuery(query, options));

/*
Returns a loading or error message if the data isn't present. Either
msg or data will be set.
*/
export const useQueryWithFarmIdAndMsg = (query, options) =>
  processResultForMessage(useQueryWithFarmId(query, options));

/*
Returns basic info for the current farm, with loading and error messages
*/
export const useFarmUpdatedAt = (skip = false) => {
  const { data, msg } = useQueryWithFarmIdAndMsg(FarmQuery, {
    skip,
    context: { priorityOp: true },
  });
  return msg || data?.farm?.updatedAt;
};

/*
Memoized map of transforms applied to the input data.
fns is an array of transform functions. The function instances
in the array must not change to avoid recomputing the transforms.
*/
export const useDataTransforms = (data, fns) =>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => (data ? fns.map((fn) => fn(data)) : []), [data, ...fns]);

/*
Returns the clientWidth of the value calculated by rendering a div
containing the value offscreen.
Style is an object of the styles to apply to the div.
Note: only changing the value will trigger a re-calculation, changing
the styles alone won't.
*/
export const useValueWidth = (initialValue, style = {}) => {
  const [value, setValue] = useState(initialValue);
  const [valueWidth, setValueWidth] = useState(0);
  useEffect(() => {
    if (value == null) return null;
    const div = document.createElement('div');
    // The styles need to be set individually to apply.
    Object.entries(style).forEach(([key, v]) => {
      div.style[key] = v;
    });
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.top = '-9999px';
    div.innerHTML = value;
    document.body.appendChild(div);
    setValueWidth(div.clientWidth);
    return () => div.remove();
  }, [style, value]);

  return [valueWidth, setValue];
};

const defaultTransformValues = (values) => ({
  ...values,
  __typename: undefined,
});
// Workaround for providing a default mutation to the useMutation hook.
// This is the minimum parseable mutation object I could come up with :)
const noopMutation = gql`
  mutation Noop($foo: String) {
    noop(foo: $foo)
  }
`;
export const useRecord = ({
  submitMutation = noopMutation,
  deleteMutation = noopMutation,
  // Fetch from a single record, used for initial record data and after resource updated
  fetchRecordQuery,
  // Refetch query called after successful deleteMutation
  onDeleteRefetchQuery,
  // Transformation function to apply to values before submitting
  transformValues = defaultTransformValues,
  /* String to redirect to after successful creation,
  use :id to insert the id of the created resource */
  creationRedirectUrl,
  deletionRedirectUrl,
  // Whether to include the farmId on the mutation input
  includeFarmId = false,
}) => {
  const { id, farmId } = useParams();
  const { msg, data } = useQueryWithMsg(fetchRecordQuery, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const resource = getFirstQueryResult(data);
  const [status, mutateAndSetState, resetMutationState] = useMutateWithState();
  const navigate = useNavigate();
  const [mutate] = useMutation(submitMutation);
  const [deleteResource] = useMutation(deleteMutation, {
    // id is used when refetching a nested list within a single resource,
    // eg. operations within a crop template
    refetchQueries: [
      { query: onDeleteRefetchQuery, variables: { id, farmId } },
    ],
    awaitRefetchQueries: true,
  });
  const onSubmit = (schema, successMessage = null) => async (
    values,
    { setSubmitting },
  ) => {
    try {
      const input = transformValues(schema.cast(values), resource);
      if (includeFarmId) {
        input.farmId = farmId;
      }
      return await mutateAndSetState(
        () =>
          mutate({
            variables: { input },
            refetchQueries: id
              ? [{ query: fetchRecordQuery, variables: { id } }]
              : [{ query: onDeleteRefetchQuery, variables: { farmId } }],
            awaitRefetchQueries: true,
          }),
        successMessage,
      ).then((response) => {
        const newId = getFirstMutationResult(response?.data)?.id;
        // If this is a create mutation, redirect to the new resource
        if (creationRedirectUrl && newId && !id) {
          navigate(generatePath(creationRedirectUrl, { id: newId }), {
            relative: 'path',
          });
        }
        setSubmitting(false);
        return response;
      });
    } catch (e) {
      // TODO: display an error?
      console.error('Casting error:', e.message);
      return Promise.reject(e);
    }
  };
  const onDeleteWithVars = (vars) => () => {
    return mutateAndSetState(() =>
      deleteResource({
        variables: { input: { id, updatedAt: resource?.updatedAt, ...vars } },
      }),
    ).then((response) => {
      if (getFirstMutationResult(response?.data) && deletionRedirectUrl) {
        navigate(deletionRedirectUrl, { relative: 'path' });
      }
    });
  };

  const onDelete = onDeleteWithVars({});

  return {
    onSubmit: submitMutation === noopMutation ? null : onSubmit,
    onDelete: deleteMutation === noopMutation ? null : onDelete,
    // Allows passing a object with extra vars for the onDelete mutation
    onDeleteWithVars: deleteMutation === noopMutation ? null : onDeleteWithVars,
    resetMutationState,
    mutateAndSetState,
    status,
    resource,
    msg,
  };
};

export const useDataScope = () => {
  const { pathname } = useLocation();
  if (getIsFarmPath(pathname)) {
    return 'farm';
  }
  if (getIsTeamPath(pathname)) {
    return 'team';
  }
  return 'superadmin';
};

export const useIsFarmView = () => {
  const location = useLocation();
  const isFarmView = useMemo(() => getIsFarmPath(location?.pathname), [
    location,
  ]);
  return isFarmView;
};

export const useIsMultiProjectDashboardView = () => {
  const { pathname } = useLocation();
  const isMultiProjectDashboardView = useMemo(
    () => pathname.startsWith('/app/dashboard'),
    [pathname],
  );
  return isMultiProjectDashboardView;
};

export const useIsPortfolioEvaluatorView = () => {
  const { pathname } = useLocation();
  return useMemo(() => pathname.startsWith('/superadmin/portfolio-evaluator'), [
    pathname,
  ]);
};

export const useIsFieldsPage = () => {
  const { pathname } = useLocation();
  return getIsFarmPath(pathname) && pathname.endsWith('/fields');
};

export const useIsSuperadminRoute = () => {
  const location = useLocation();
  const isSuperadminRoute = useMemo(
    () => location.pathname.startsWith('/superadmin'),
    [location],
  );
  return isSuperadminRoute;
};

/* Returns the browser URLSearchParams object */
export const useUrlQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const setIntercomHidden = (hidden) => {
  if (window.Intercom) {
    window.Intercom('update', {
      hide_default_launcher: hidden,
    });
  }
};

export const useHideIntercom = () => {
  useEffect(() => {
    setIntercomHidden(true);
    return () => {
      setIntercomHidden(false);
    };
  }, []);
};

// Sets the farm source in the FarmSourceContext when the farmId changes
export const useSetFarmSourceEffect = () => {
  const { farmId } = useParams();
  const { state } = useLocation();

  const { setSource } = useFarmSourceContext();

  useEffect(() => {
    if (!getIsFarmPath(state?.source)) {
      setSource(state?.source);
    }
  }, [farmId]); // eslint-disable-line react-hooks/exhaustive-deps
};

// similar to resource at https://usehooks.com/useKeyPress/
export const useKeyPress = (targetKey) => {
  const [isKeyPressed, setIsKeyPressed] = useState(false);

  const handleKeyDown = ({ key }) => {
    if (targetKey === key) {
      setIsKeyPressed(true);
    }
  };

  const handleKeyUp = ({ key }) => {
    if (targetKey === key) {
      setIsKeyPressed(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return isKeyPressed;
};

export const useToggleState = (initialState = false) => {
  const [state, setState] = useState(initialState);
  const toggleState = useCallback(() => setState((current) => !current), []);
  return [state, toggleState];
};
